import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Sparkles, AlertTriangle, Check, Loader2, Pencil, BookMarked, PenLine, Globe, Lock, Link2, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PhotoStrip } from "./DiaryPhotos";

const inp = "border border-zinc-300 px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary bg-white";
const CATS = ["flight", "airport", "train", "bus", "taxi", "rental_car", "driving", "walking", "hotel",
  "check_in", "check_out", "restaurant", "breakfast", "lunch", "dinner", "coffee", "attraction",
  "sightseeing", "tour", "museum", "shopping", "meeting", "family_friends", "festival",
  "entertainment", "hiking", "beach", "photography", "medical", "rest", "free_time", "other"];

/* One expanded day: timeline, plan-your-day form, AI planner, tasks, diary entry.
   readOnly = viewer has view-only access to this shared trip.
   refreshSignal = live co-editing tick; reloads the day without clobbering unsaved typing. */
export function DiaryDay({ tripId, date, city, onChanged, readOnly = false, refreshSignal = 0 }) {
  const [day, setDay] = useState(null);
  const [form, setForm] = useState(null);
  const [entry, setEntry] = useState("");
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const serverTextRef = useRef("");

  const load = useCallback(() => api.get(`/diary/${tripId}/day/${date}`).then((r) => {
    setDay(r.data);
    const t = r.data.entry?.text || "";
    // only sync the textarea when the user has no unsaved local edits
    setEntry((cur) => (cur === serverTextRef.current ? t : cur));
    serverTextRef.current = t;
  }).catch(() => {}), [tripId, date]);
  useEffect(() => { load(); }, [load, refreshSignal]);
  const changed = () => { load(); onChanged?.(); };

  const saveAct = async (data) => {
    if (!String(data.title || "").trim()) { toast.error("Activity title required"); return; }
    await api.post(`/diary/${tripId}/items/activity`, { ...data, date });
    setForm(null); changed(); toast.success(data.id ? "Activity updated" : "Activity saved");
  };
  const saveEntry = async () => {
    await api.post(`/diary/${tripId}/entry`, { date, text: entry });
    toast.success("Diary entry saved"); changed();
  };
  const aiPlan = async () => {
    setAiBusy(true);
    try { const r = await api.post(`/diary/${tripId}/day/${date}/ai-plan`, {}); setAi(r.data); }
    catch { toast.error("AI planner unavailable — try again"); }
    setAiBusy(false);
  };

  if (!day) return <div className="border border-t-0 border-zinc-300 bg-zinc-50 p-4 text-xs text-muted-foreground">Loading…</div>;

  return (
    <div className="border border-t-0 border-indigo-200 bg-indigo-50/30 p-4 space-y-4" data-testid={`diary-day-detail-${date}`}>
      {day.warnings.map((w, i) => (
        <div key={i} className="flex items-start gap-2 border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`diary-warning-${i}`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {w.message}
        </div>
      ))}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-700 text-sm">Timeline{city ? ` — ${city}` : ""}</h3>
          {!readOnly && <div className="flex gap-1.5">
            <button onClick={aiPlan} disabled={aiBusy} data-testid={`diary-ai-plan-${date}`} className="inline-flex items-center gap-1 text-xs font-600 text-indigo-700 border border-indigo-300 bg-white px-2 py-1">
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Help me plan this day
            </button>
            <button onClick={() => setForm(form ? null : { title: "", start_time: "", end_time: "", category: "", location: "", notes: "" })} data-testid={`diary-add-activity-${date}`}
              className="inline-flex items-center gap-1 text-xs font-600 text-white bg-indigo-600 px-2 py-1"><Plus className="w-3.5 h-3.5" /> Add activity</button>
          </div>}
        </div>
        {form && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 bg-white border border-zinc-200 p-2 mb-2" data-testid={`diary-activity-form-${date}`}>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Activity title" data-testid="diary-f-title" className={`${inp} col-span-2 sm:col-span-3`} />
            <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="diary-f-start" className={inp} />
            <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={inp} />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="diary-f-category" className={inp}>
              <option value="">Category…</option>{CATS.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
            </select>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className={inp} />
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes / reservation no." className={`${inp} col-span-2`} />
            <button onClick={() => saveAct(form)} data-testid="diary-f-save" className="col-span-2 sm:col-span-3 bg-indigo-600 text-white text-xs font-600 py-1.5">{form.id ? "Update activity" : "Save activity"}</button>
          </div>
        )}
        {day.activities.length === 0 && !form ? (
          <p className="text-xs text-muted-foreground" data-testid={`diary-empty-${date}`}>No activities planned — use "Add activity" or let AI help.</p>
        ) : (
          <ul className="space-y-1">
            {day.activities.map((a) => (
              <li key={a.id} className="bg-white border border-zinc-200 px-2.5 py-1.5 text-sm" data-testid={`diary-activity-${a.id}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-indigo-700 w-24 shrink-0">{a.all_day ? "all day" : `${(a.start_time || "").slice(0, 5)}${a.end_time ? `–${a.end_time.slice(0, 5)}` : ""}`}</span>
                  <span className="font-600 truncate">{a.title}</span>
                  {a.category && <span className="text-[10px] uppercase bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded shrink-0">{a.category.replace("_", " ")}</span>}
                  {a.location && <span className="text-xs text-muted-foreground truncate">{a.location}</span>}
                  {a.author_name && <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full shrink-0" title={a.updated_by_name ? `Last edited by ${a.updated_by_name}` : undefined} data-testid={`diary-activity-author-${a.id}`}>by {a.author_name}</span>}
                  {!readOnly && <>
                    <button onClick={() => setForm({ id: a.id, title: a.title || "", start_time: a.start_time || "", end_time: a.end_time || "", category: a.category || "", location: a.location || "", notes: a.notes || "" })}
                      data-testid={`diary-activity-edit-${a.id}`} className="ml-auto text-muted-foreground hover:text-indigo-700 shrink-0"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={async () => { await api.delete(`/diary/${tripId}/items/activity/${a.id}`); changed(); }} data-testid={`diary-activity-del-${a.id}`} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>}
                </div>
                <div className="mt-1.5 pl-0.5">
                  <PhotoStrip tripId={tripId} kind="activity" iid={a.id} photos={a.photos || []} onChanged={changed} readOnly={readOnly} />
                </div>
              </li>
            ))}
          </ul>
        )}
        {ai && (
          <div className="mt-2 border border-indigo-200 bg-white p-2.5" data-testid={`diary-ai-suggestions-${date}`}>
            <div className="text-xs font-700 text-indigo-800 mb-1">AI suggestions — nothing is added until you approve:</div>
            {ai.suggestions.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-1 border-t border-zinc-100">
                <span className="font-mono text-indigo-700 w-20">{s.start_time}{s.end_time ? `–${s.end_time}` : ""}</span>
                <span className="font-600">{s.title}</span>
                <span className="text-muted-foreground truncate">{s.notes}</span>
                <button onClick={async () => { await saveAct({ ...s }); }} data-testid={`diary-ai-accept-${i}`} className="ml-auto inline-flex items-center gap-1 text-emerald-700 border border-emerald-300 px-1.5 py-0.5 shrink-0"><Check className="w-3 h-3" /> Add</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {day.tasks.length > 0 && (
        <div>
          <h3 className="font-700 text-sm mb-1">Tasks</h3>
          {day.tasks.map((t) => (
            <label key={t.id} className="flex items-center gap-2 text-sm py-0.5">
              <input type="checkbox" checked={!!t.done} disabled={readOnly} onChange={async () => { await api.post(`/diary/${tripId}/items/task/${t.id}/toggle`); changed(); }} data-testid={`diary-task-toggle-${t.id}`} />
              <span className={t.done ? "line-through text-muted-foreground" : ""}>{t.title}</span>
            </label>
          ))}
        </div>
      )}

      {Object.keys(day.spend || {}).length > 0 && (
        <div className="text-xs text-muted-foreground">Today's spending: {Object.entries(day.spend).map(([c, v]) => `${c} ${Number(v).toLocaleString()}`).join(" · ")}</div>
      )}

      <div>
        <h3 className="font-700 text-sm mb-1">Today's Diary <span className="font-400 text-xs text-muted-foreground">— shared, everyone on this trip writes in the same entry</span></h3>
        <textarea value={entry} onChange={(e) => setEntry(e.target.value)} onBlur={() => { if (!readOnly && entry !== (day.entry?.text || "")) saveEntry(); }} rows={4} data-testid={`diary-entry-text-${date}`}
          disabled={readOnly}
          placeholder={readOnly ? "View-only access — ask the trip owner for edit access to write here." : "What did you do today? Where did you go? What did you eat? What should you remember for next time?"}
          className="w-full border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary bg-white disabled:bg-zinc-100 disabled:text-muted-foreground disabled:cursor-not-allowed disabled:border-dashed" />
        <div className="flex items-center gap-2 mt-1">
          {!readOnly && <button onClick={saveEntry} data-testid={`diary-entry-save-${date}`} className="px-3 py-1.5 bg-primary text-white text-xs font-600">Save diary entry</button>}
          {!readOnly && entry !== (day.entry?.text || "") && <span className="text-[11px] text-amber-700 font-600" data-testid={`diary-entry-dirty-${date}`}>Unsaved changes — saved automatically when you click away</span>}
          {day.entry?.updated_by_name && <span className="text-[11px] text-muted-foreground ml-auto" data-testid={`diary-entry-editor-${date}`}>Last edited by {day.entry.updated_by_name}</span>}
        </div>
        {day.entry?.id && (
          <div className="mt-2">
            <PhotoStrip tripId={tripId} kind="entry" iid={day.entry.id} photos={day.entry.photos || []} onChanged={changed} readOnly={readOnly} />
          </div>
        )}
        <EntryLinks tripId={tripId} date={date} links={day.entry?.links || []} onChanged={changed} readOnly={readOnly} />
      </div>
    </div>
  );
}

/* Per-day references — every day can hold its own links (maps, tickets, blogs…). */
function EntryLinks({ tripId, date, links, onChanged, readOnly }) {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const save = async (next) => { await api.post(`/diary/${tripId}/entry`, { date, links: next }); onChanged(); };
  const add = async () => {
    const u = url.trim();
    if (!u) return;
    const full = /^https?:\/\//i.test(u) ? u : `https://${u}`;
    const norm = (x) => x.toLowerCase().replace(/\/+$/, "");
    if ((links || []).some((l) => norm(l.url) === norm(full))) { toast.error("This link is already on this day — duplicate rejected"); setUrl(""); return; }
    try { await save([...(links || []), { title: title.trim(), url: full }]); setTitle(""); setUrl(""); toast.success("Link added to this day"); }
    catch { toast.error("Could not add link"); }
  };
  const remove = async (i) => { try { await save(links.filter((_, idx) => idx !== i)); } catch { toast.error("Could not remove link"); } };
  return (
    <div className="mt-2" data-testid={`diary-entry-links-${date}`}>
      {(links || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {links.map((l, i) => (
            <span key={l.id || i} className="inline-flex items-center gap-1 text-xs font-600 text-indigo-700 border border-indigo-200 bg-indigo-50/60 px-2 py-0.5" data-testid={`diary-entry-link-${date}-${i}`}>
              <ExternalLink className="w-3 h-3" />
              <a href={l.url} target="_blank" rel="noreferrer" className="hover:underline max-w-[180px] truncate">
                {l.title || (() => { try { return new URL(l.url).hostname.replace(/^www\./, ""); } catch { return l.url; } })()}
              </a>
              {!readOnly && <button onClick={() => remove(i)} data-testid={`diary-entry-link-del-${date}-${i}`} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>}
            </span>
          ))}
        </div>
      )}
      {!readOnly && (
        <div className="flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Link2 className="w-3.5 h-3.5" /> Day links:</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid={`diary-entry-link-title-${date}`} className={`${inp} w-36`} />
          <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Paste a link — map, tickets, blog…" data-testid={`diary-entry-link-url-${date}`} className={`${inp} flex-1 min-w-[180px]`} />
          <button onClick={add} data-testid={`diary-entry-link-add-${date}`} className="px-2.5 py-1.5 text-xs font-600 border border-zinc-300 bg-white hover:border-indigo-500">+ Add</button>
        </div>
      )}
    </div>
  );
}

function GenericPanel({ tripId, kind, fields, render, addLabel, testid, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get(`/diary/${tripId}/items/${kind}`).then((r) => setItems(r.data.items)).catch(() => {}), [tripId, kind]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!Object.values(form).some((v) => String(v || "").trim())) return;
    await api.post(`/diary/${tripId}/items/${kind}`, form); setForm(null); load(); toast.success("Saved");
  };
  return (
    <div data-testid={testid}>
      {!readOnly && <button onClick={() => setForm(form ? null : Object.fromEntries(fields.map((f) => [f.k, ""])))} data-testid={`${testid}-add`}
        className="inline-flex items-center gap-1 mb-3 px-3 py-1.5 bg-indigo-600 text-white text-xs font-600"><Plus className="w-3.5 h-3.5" /> {addLabel}</button>}
      {form && (
        <div className="grid sm:grid-cols-3 gap-1.5 bg-white border border-zinc-200 p-2 mb-3">
          {fields.map((f) => f.type === "select"
            ? <select key={f.k} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`${testid}-f-${f.k}`} className={inp}><option value="">{f.ph}</option>{f.opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>
            : <input key={f.k} type={f.type || "text"} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder={f.ph} data-testid={`${testid}-f-${f.k}`} className={inp} />)}
          <button onClick={save} data-testid={`${testid}-save`} className="sm:col-span-3 bg-indigo-600 text-white text-xs font-600 py-1.5">Save</button>
        </div>
      )}
      {items.length === 0 ? <p className="text-sm text-muted-foreground">Nothing here yet.</p> : (
        <ul className="space-y-1.5">{items.map((it) => (
          <li key={it.id} className="flex items-center gap-2 bg-white border border-zinc-200 px-3 py-2 text-sm" data-testid={`${testid}-item-${it.id}`}>
            {render(it)}
            {it.author_name && <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full shrink-0">by {it.author_name}</span>}
            {!readOnly && <button onClick={async () => { await api.delete(`/diary/${tripId}/items/${kind}/${it.id}`); load(); }} data-testid={`${testid}-del-${it.id}`} className="ml-auto text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>}
          </li>
        ))}</ul>
      )}
    </div>
  );
}

export const PlacesPanel = ({ tripId, readOnly }) => (
  <GenericPanel tripId={tripId} readOnly={readOnly} kind="place" testid="diary-places" addLabel="Add place"
    fields={[{ k: "country", ph: "Country" }, { k: "region", ph: "State / region" }, { k: "city", ph: "City" },
      { k: "area", ph: "Neighborhood / town" }, { k: "name", ph: "Specific place" }, { k: "arrival", ph: "Arrival", type: "date" }, { k: "notes", ph: "Notes" }]}
    render={(p) => <span className="truncate">{[p.country, p.region, p.city, p.area, p.name].filter(Boolean).join(" → ")}{p.arrival ? ` · ${p.arrival}` : ""}{p.notes ? ` — ${p.notes}` : ""}</span>} />
);

export const LinksPanel = ({ tripId, readOnly }) => (
  <GenericPanel tripId={tripId} readOnly={readOnly} kind="link" testid="diary-links" addLabel="Save a link"
    fields={[{ k: "title", ph: "Title" }, { k: "url", ph: "https://…" },
      { k: "category", ph: "Category…", type: "select", opts: ["attraction", "restaurant", "hotel", "airline", "maps", "youtube", "blog", "reservation", "tickets", "other"] },
      { k: "date", ph: "Related day", type: "date" }, { k: "notes", ph: "Notes" }]}
    render={(l) => <><a href={l.url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate">{l.title || l.url}</a>{l.category && <span className="text-[10px] uppercase bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">{l.category}</span>}{l.date && <span className="text-xs text-muted-foreground">{l.date}</span>}</>} />
);

export function TasksPanel({ tripId, onChanged, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const load = useCallback(() => api.get(`/diary/${tripId}/items/task`).then((r) => setItems(r.data.items)).catch(() => {}), [tripId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!title.trim()) return;
    await api.post(`/diary/${tripId}/items/task`, { title, date: date || null });
    setTitle(""); load(); onChanged?.();
  };
  return (
    <div data-testid="diary-tasks">
      {!readOnly && <div className="flex gap-1.5 mb-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Download boarding passes" data-testid="diary-task-input" className={`${inp} flex-1`} />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="diary-task-date" className={inp} />
        <button onClick={add} data-testid="diary-task-add" className="px-3 bg-indigo-600 text-white text-xs font-600">Add</button>
      </div>}
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No tasks yet — add packing, bookings or reminders.</p> :
        items.map((t) => (
          <label key={t.id} className="flex items-center gap-2 text-sm py-1 border-b border-zinc-100" data-testid={`diary-task-${t.id}`}>
            <input type="checkbox" checked={!!t.done} disabled={readOnly} onChange={async () => { await api.post(`/diary/${tripId}/items/task/${t.id}/toggle`); load(); onChanged?.(); }} data-testid={`diary-tasks-toggle-${t.id}`} />
            <span className={t.done ? "line-through text-muted-foreground" : ""}>{t.title}</span>
            {t.date && <span className="text-xs text-muted-foreground">{t.date}</span>}
            {t.author_name && <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full">by {t.author_name}</span>}
            {!readOnly && <button onClick={async () => { await api.delete(`/diary/${tripId}/items/task/${t.id}`); load(); }} data-testid={`diary-tasks-del-${t.id}`} className="ml-auto text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}
          </label>
        ))}
    </div>
  );
}

export function StoryPanel({ tripId }) {
  const [stories, setStories] = useState([]);
  const [pubStories, setPubStories] = useState([]);
  const [style, setStyle] = useState("Travel Journal");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/diary/${tripId}/stories`).then((r) => setStories(r.data.items)).catch(() => {});
    api.get("/stories/mine").then((r) => setPubStories(r.data.items.filter((s) => s.trip_id === tripId && s.status !== "archived"))).catch(() => {});
  }, [tripId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try { await api.post(`/diary/${tripId}/story`, { style }); load(); toast.success("Story drafted — private until you share it"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create story"); }
    setBusy(false);
  };
  return (
    <div data-testid="diary-story">
      {/* Full publisher: blog / story / social / PDF with Privacy Shield */}
      <div className="border border-fuchsia-300 bg-gradient-to-br from-fuchsia-50 to-indigo-50 p-4 mb-4" data-testid="diary-story-publisher-card">
        <div className="flex flex-wrap items-center gap-3">
          <BookMarked className="w-8 h-8 text-fuchsia-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <h3 className="font-heading font-900 text-base">Create Travel Story / Blog / PDF</h3>
            <p className="text-xs text-muted-foreground">Turn selected diary days & photos into a shareable story. Privacy Shield removes booking numbers, emails and documents automatically — your diary stays private.</p>
          </div>
          <Link to={`/trips/${tripId}/create-story`} data-testid="diary-create-story-btn"
            className="inline-flex items-center gap-1.5 bg-fuchsia-600 text-white px-4 py-2 text-sm font-700 shrink-0"><Sparkles className="w-4 h-4" /> Create Travel Story</Link>
        </div>
        {pubStories.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {pubStories.map((s) => (
              <div key={s.id} className="flex items-center gap-2 bg-white border border-zinc-200 px-3 py-2 text-sm" data-testid={`diary-pubstory-${s.id}`}>
                {s.status === "published" ? <Globe className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <Lock className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                <span className="font-600 truncate">{s.title}</span>
                <span className="text-[10px] uppercase text-muted-foreground shrink-0">{s.status === "published" ? s.visibility : "draft"}</span>
                <Link to={`/stories/${s.id}/edit`} data-testid={`diary-pubstory-edit-${s.id}`} className="ml-auto inline-flex items-center gap-1 text-xs font-700 text-indigo-700 shrink-0"><PenLine className="w-3 h-3" /> Edit / Share</Link>
              </div>
            ))}
          </div>
        )}
        <Link to="/my-stories" className="inline-block mt-2 text-xs text-primary hover:underline" data-testid="diary-my-stories-link">All my travel stories →</Link>
      </div>

      <div className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-2">Quick AI draft (text only, stays here)</div>
      <div className="flex gap-1.5 mb-3">
        <select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="diary-story-style" className={inp}>
          {["Personal Diary", "Travel Journal", "Family Memories", "Travel Blog", "Short Summary", "Photo Story"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <button onClick={create} disabled={busy} data-testid="diary-story-create" className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-xs font-600 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Create My Travel Story
        </button>
      </div>
      {stories.length === 0 ? <p className="text-sm text-muted-foreground">No quick drafts yet — write some diary entries, then let AI draft your travel story. It stays private.</p> :
        stories.map((s) => (
          <div key={s.id} className="border border-zinc-300 bg-white p-4 mb-3" data-testid={`diary-story-${s.id}`}>
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-heading font-900">{s.title} <span className="text-xs font-400 text-muted-foreground">· {s.style}</span></h3>
              <button onClick={async () => { await api.delete(`/diary/${tripId}/stories/${s.id}`); load(); }} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="text-sm mt-2 whitespace-pre-line">{s.story}</div>
          </div>
        ))}
    </div>
  );
}
